import { Builds_BuildFailReason, Builds_BuildResult, Builds_SortField, Builds_SortOrder } from '@/graphql/codegen/generated/graphql';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { computed } from 'vue';
import { useOsService } from '@/services/OsService/OsService';

// eslint-disable-next-line no-shadow
export const enum WORKLOADS_DURATION_UNITS {
  Minutes = 'Minutes',
  Hours = 'Hours',
  Seconds = 'Seconds',
}

export const LOG_LEVEL_WORKLOADS = {
  1: '1 - Minimal',
  2: '2 - Basic',
  3: '3 - Intermediate',
  4: '4 - Extended',
  5: '5 - Detailed',
};

// eslint-disable-next-line no-shadow
export enum DisplayBuildResult {
  Canceled = 'Canceled',
  Failed = 'Failed',
  Succeeded = 'Succeeded',
}
export const workloadsResultToDisplayMapping = {
  [Builds_BuildResult.Canceled]: DisplayBuildResult.Canceled,
  [Builds_BuildResult.Failed]: DisplayBuildResult.Failed,
  [Builds_BuildResult.Success]: DisplayBuildResult.Succeeded,
};

export const displayToWorkloadsResultMapping = {
  [DisplayBuildResult.Canceled]: Builds_BuildResult.Canceled,
  [DisplayBuildResult.Failed]: Builds_BuildResult.Failed,
  [DisplayBuildResult.Succeeded]: Builds_BuildResult.Success,
};

export const useWorkloadsTableConstants = () => {
  const osService = useOsService();
  const os = computed(() => osService?.osType.value);

  const windowsColumnsNames = {
    buildResult: 'Status',
    title: 'Build Title',
    commandLine: 'Command Line',
    initiatorName: 'Initiator Name',
    buildGroup: 'Build Group',
    initiatorType: 'Initiator Type',
    buildType: 'Build Type',
    startTime: 'Start Time',
    duration: 'Duration',
    failReason: 'Failure Reason',
    totalTasks: 'Total Tasks',
    localTasks: 'Local Tasks',
    cloudTasks: 'Cloud Tasks',
    remoteTasks: 'Remote Tasks',
    maxBusyHelpersCores: 'Max. Helper Cores',
    avgBusyHelpersCores: 'Avg. Helper Cores',
    maxNeededHelpersCores: 'Max. Needed Helper Cores',
    cacheTaskHits: 'Hit',
    cacheTaskMiss: 'Miss',
    uncacheableTasks: 'Uncacheable Tasks',
    loggedOnUsers: 'Logged On user',
    agentDescription: 'Agent Description',
    loggingLevel: 'Log Level',
    failedTasks: 'Failed Tasks',
    distributableTasks: 'Distributable Tasks',
    readyOverActiveRatio: 'Unmet Helper Requests',
  };

  const linuxColumnsNames = {
    buildResult: 'Status', //
    title: 'Build Caption',
    commandLine: 'Command Line',
    initiatorName: 'Initiator Name',
    buildGroup: 'Build Group',
    startTime: 'Start Time',
    duration: 'Duration',
    failReason: 'Failure Reason',
    totalTasks: 'Total Tasks',
    localTasks: 'Local Tasks',
    cloudTasks: 'Cloud Tasks',
    remoteTasks: 'Remote Tasks',
    maxBusyHelpersCores: 'Max. Helper Cores',
    avgBusyHelpersCores: 'Avg. Helper Cores',
    maxNeededHelpersCores: 'Max. Needed Helper Cores',
  };

  const macColumnsNames = {
    buildResult: 'Status',
    title: 'Build Caption',
    commandLine: 'Command Line',
    initiatorName: 'Initiator Name',
    buildGroup: 'Build Group',
    startTime: 'Start Time',
    duration: 'Duration',
    failReason: 'Failure Reason',
    totalTasks: 'Total Tasks',
    localTasks: 'Local Tasks',
    remoteTasks: 'Remote Tasks',
    maxBusyHelpersCores: 'Max. Helper Cores',
    avgBusyHelpersCores: 'Avg. Helper Cores',
    maxNeededHelpersCores: 'Max. Needed Helper Cores',
  };

  const COLUMNS_NAMES = {
    [OsType.Windows]: windowsColumnsNames,
    [OsType.Linux]: linuxColumnsNames,
    [OsType.Mac]: macColumnsNames,
  };

  const workloadsColumnsWindows = [
    // default
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Windows].buildResult, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Windows].startTime, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Windows].duration, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Windows].initiatorName, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Windows].title, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Windows].commandLine, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 128, name: COLUMNS_NAMES[OsType.Windows].failedTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Windows].maxNeededHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Windows].maxBusyHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Windows].readyOverActiveRatio, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    // non default
    { minWidth: 72, width: 160, name: COLUMNS_NAMES[OsType.Windows].loggedOnUsers, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 168, name: COLUMNS_NAMES[OsType.Windows].loggingLevel, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 208, name: COLUMNS_NAMES[OsType.Windows].agentDescription, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Windows].remoteTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending }, // remote
    { minWidth: 72, width: 120, name: COLUMNS_NAMES[OsType.Windows].totalTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 136, name: COLUMNS_NAMES[OsType.Windows].localTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 128, name: COLUMNS_NAMES[OsType.Windows].cloudTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Windows].distributableTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 128, name: COLUMNS_NAMES[OsType.Windows].buildGroup, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Windows].failReason, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Windows].buildType, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 120, name: COLUMNS_NAMES[OsType.Windows].cacheTaskHits, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 120, name: COLUMNS_NAMES[OsType.Windows].cacheTaskMiss, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Windows].uncacheableTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 168, name: COLUMNS_NAMES[OsType.Windows].avgBusyHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 128, name: COLUMNS_NAMES[OsType.Windows].initiatorType, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
  ];

  const workloadsColumnsLinux = [
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Linux].buildResult, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Linux].startTime, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Linux].duration, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Linux].initiatorName, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Linux].title, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Linux].commandLine, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Linux].maxNeededHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Linux].maxBusyHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Linux].remoteTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending }, // remote
    { minWidth: 72, width: 120, name: COLUMNS_NAMES[OsType.Linux].totalTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 136, name: COLUMNS_NAMES[OsType.Linux].localTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 128, name: COLUMNS_NAMES[OsType.Linux].cloudTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 128, name: COLUMNS_NAMES[OsType.Linux].buildGroup, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Linux].failReason, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 168, name: COLUMNS_NAMES[OsType.Linux].avgBusyHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
  ];

  const workloadsColumnsMac = [
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Linux].buildResult, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Linux].startTime, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Linux].duration, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Linux].initiatorName, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Linux].title, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 240, name: COLUMNS_NAMES[OsType.Linux].commandLine, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Linux].maxNeededHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Linux].maxBusyHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 144, name: COLUMNS_NAMES[OsType.Linux].remoteTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending }, // remote
    { minWidth: 72, width: 120, name: COLUMNS_NAMES[OsType.Linux].totalTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 136, name: COLUMNS_NAMES[OsType.Linux].localTasks, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
    { minWidth: 72, width: 128, name: COLUMNS_NAMES[OsType.Linux].buildGroup, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 176, name: COLUMNS_NAMES[OsType.Linux].failReason, sortable: true, defaultSortOrder: Builds_SortOrder.Ascending },
    { minWidth: 72, width: 168, name: COLUMNS_NAMES[OsType.Linux].avgBusyHelpersCores, sortable: true, defaultSortOrder: Builds_SortOrder.Descending },
  ];

  const WORKLOADS_COLUMNS = {
    [OsType.Windows]: workloadsColumnsWindows,
    [OsType.Linux]: workloadsColumnsLinux,
    [OsType.Mac]: workloadsColumnsMac,
  };

  const WORKLOADS_FAIL_REASON = {
    [Builds_BuildFailReason.CancelledByUser]: 'Cancelled by user',
    [Builds_BuildFailReason.Failure]: 'Failure',
    [Builds_BuildFailReason.ForcedStop]: 'Forced stop',
    [Builds_BuildFailReason.MaxConcurrentBuilds]: 'Max concurrent builds',
    [Builds_BuildFailReason.NotEnoughCores]: 'Not enough cores',
    [Builds_BuildFailReason.Pending]: 'Pending',
    [Builds_BuildFailReason.SystemError]: 'System error',
  };

  const columnsToSortMapWindows = {
    [COLUMNS_NAMES[OsType.Windows].buildResult]: Builds_SortField.BuildResult,
    [COLUMNS_NAMES[OsType.Windows].title]: Builds_SortField.BuildTitle,
    [COLUMNS_NAMES[OsType.Windows].commandLine]: Builds_SortField.CommandLine,
    [COLUMNS_NAMES[OsType.Windows].initiatorName]: Builds_SortField.InitiatorName,
    [COLUMNS_NAMES[OsType.Windows].initiatorType]: Builds_SortField.InitiatorType,
    [COLUMNS_NAMES[OsType.Windows].buildGroup]: Builds_SortField.BuildGroup,
    [COLUMNS_NAMES[OsType.Windows].buildType]: Builds_SortField.BuiltType,
    [COLUMNS_NAMES[OsType.Windows].startTime]: Builds_SortField.StartTime,
    [COLUMNS_NAMES[OsType.Windows].duration]: Builds_SortField.Duration,
    [COLUMNS_NAMES[OsType.Windows].failReason]: Builds_SortField.FailReason,
    [COLUMNS_NAMES[OsType.Windows].remoteTasks]: Builds_SortField.RemoteTasks,
    [COLUMNS_NAMES[OsType.Windows].totalTasks]: Builds_SortField.TotalTasks,
    [COLUMNS_NAMES[OsType.Windows].localTasks]: Builds_SortField.LocalTasks,
    [COLUMNS_NAMES[OsType.Windows].cloudTasks]: Builds_SortField.CloudTasks,
    [COLUMNS_NAMES[OsType.Windows].avgBusyHelpersCores]: Builds_SortField.AvgBusyHelpersCores,
    [COLUMNS_NAMES[OsType.Windows].maxBusyHelpersCores]: Builds_SortField.MaxBusyHelpersCores,
    [COLUMNS_NAMES[OsType.Windows].maxNeededHelpersCores]: Builds_SortField.MaxNeededHelpersCores,
    [COLUMNS_NAMES[OsType.Windows].cacheTaskHits]: Builds_SortField.CacheTaskHits,
    [COLUMNS_NAMES[OsType.Windows].cacheTaskMiss]: Builds_SortField.CacheTaskMiss,
    [COLUMNS_NAMES[OsType.Windows].uncacheableTasks]: Builds_SortField.UncacheableTasks,
    [COLUMNS_NAMES[OsType.Windows].agentDescription]: Builds_SortField.AgentDescription,
    [COLUMNS_NAMES[OsType.Windows].loggedOnUsers]: Builds_SortField.LoggedOnUsers,
    [COLUMNS_NAMES[OsType.Windows].loggingLevel]: Builds_SortField.LoggingLevel,
    [COLUMNS_NAMES[OsType.Windows].failedTasks]: Builds_SortField.FailedTasks,
    [COLUMNS_NAMES[OsType.Windows].distributableTasks]: Builds_SortField.DistributableTasks,
    [COLUMNS_NAMES[OsType.Windows].readyOverActiveRatio]: Builds_SortField.ReadyOverActiveRatio,
  };

  const columnsToSortMapLinux = {
    [COLUMNS_NAMES[OsType.Linux].buildResult]: Builds_SortField.BuildResult,
    [COLUMNS_NAMES[OsType.Linux].title]: Builds_SortField.BuildTitle,
    [COLUMNS_NAMES[OsType.Linux].commandLine]: Builds_SortField.CommandLine,
    [COLUMNS_NAMES[OsType.Linux].initiatorName]: Builds_SortField.InitiatorName,
    [COLUMNS_NAMES[OsType.Linux].buildGroup]: Builds_SortField.BuildGroup,
    [COLUMNS_NAMES[OsType.Linux].startTime]: Builds_SortField.StartTime,
    [COLUMNS_NAMES[OsType.Linux].duration]: Builds_SortField.Duration,
    [COLUMNS_NAMES[OsType.Linux].failReason]: Builds_SortField.FailReason,
    [COLUMNS_NAMES[OsType.Linux].remoteTasks]: Builds_SortField.RemoteTasks,
    [COLUMNS_NAMES[OsType.Linux].totalTasks]: Builds_SortField.TotalTasks,
    [COLUMNS_NAMES[OsType.Linux].localTasks]: Builds_SortField.LocalTasks,
    [COLUMNS_NAMES[OsType.Linux].cloudTasks]: Builds_SortField.CloudTasks,
    [COLUMNS_NAMES[OsType.Linux].avgBusyHelpersCores]: Builds_SortField.AvgBusyHelpersCores,
    [COLUMNS_NAMES[OsType.Linux].maxBusyHelpersCores]: Builds_SortField.MaxBusyHelpersCores,
    [COLUMNS_NAMES[OsType.Linux].maxNeededHelpersCores]: Builds_SortField.MaxNeededHelpersCores,
  };

  const WORKLOADS_COLUMNS_TO_SORT_MAP = {
    [OsType.Windows]: columnsToSortMapWindows,
    [OsType.Linux]: columnsToSortMapLinux,
    [OsType.Mac]: columnsToSortMapLinux,
  };

  const sortToColumnsWindows = {
    [Builds_SortField.BuildResult]: COLUMNS_NAMES[OsType.Windows].buildResult,
    [Builds_SortField.BuildTitle]: COLUMNS_NAMES[OsType.Windows].title,
    [Builds_SortField.CommandLine]: COLUMNS_NAMES[OsType.Windows].commandLine,
    [Builds_SortField.InitiatorName]: COLUMNS_NAMES[OsType.Windows].initiatorName,
    [Builds_SortField.InitiatorType]: COLUMNS_NAMES[OsType.Windows].initiatorType,
    [Builds_SortField.BuildGroup]: COLUMNS_NAMES[OsType.Windows].buildGroup,
    [Builds_SortField.BuiltType]: COLUMNS_NAMES[OsType.Windows].buildType,
    [Builds_SortField.StartTime]: COLUMNS_NAMES[OsType.Windows].startTime,
    [Builds_SortField.Duration]: COLUMNS_NAMES[OsType.Windows].duration,
    [Builds_SortField.FailReason]: COLUMNS_NAMES[OsType.Windows].failReason,
    [Builds_SortField.RemoteTasks]: COLUMNS_NAMES[OsType.Windows].remoteTasks,
    [Builds_SortField.TotalTasks]: COLUMNS_NAMES[OsType.Windows].totalTasks,
    [Builds_SortField.LocalTasks]: COLUMNS_NAMES[OsType.Windows].localTasks,
    [Builds_SortField.CloudTasks]: COLUMNS_NAMES[OsType.Windows].cloudTasks,
    [Builds_SortField.AvgBusyHelpersCores]: COLUMNS_NAMES[OsType.Windows].avgBusyHelpersCores,
    [Builds_SortField.MaxBusyHelpersCores]: COLUMNS_NAMES[OsType.Windows].maxBusyHelpersCores,
    [Builds_SortField.MaxNeededHelpersCores]: COLUMNS_NAMES[OsType.Windows].maxNeededHelpersCores,
    [Builds_SortField.CacheTaskMiss]: COLUMNS_NAMES[OsType.Windows].cacheTaskMiss,
    [Builds_SortField.CacheTaskHits]: COLUMNS_NAMES[OsType.Windows].cacheTaskHits,
    [Builds_SortField.UncacheableTasks]: COLUMNS_NAMES[OsType.Windows].uncacheableTasks,
    [Builds_SortField.AgentDescription]: COLUMNS_NAMES[OsType.Windows].agentDescription,
    [Builds_SortField.LoggedOnUsers]: COLUMNS_NAMES[OsType.Windows].loggedOnUsers,
    [Builds_SortField.LoggingLevel]: COLUMNS_NAMES[OsType.Windows].loggingLevel,
    [Builds_SortField.FailedTasks]: COLUMNS_NAMES[OsType.Windows].failedTasks,
    [Builds_SortField.DistributableTasks]: COLUMNS_NAMES[OsType.Windows].distributableTasks,
    [Builds_SortField.ReadyOverActiveRatio]: COLUMNS_NAMES[OsType.Windows].readyOverActiveRatio,
  };

  const sortToColumnsLinux = {
    [Builds_SortField.BuildResult]: COLUMNS_NAMES[OsType.Linux].buildResult,
    [Builds_SortField.BuildTitle]: COLUMNS_NAMES[OsType.Linux].title,
    [Builds_SortField.CommandLine]: COLUMNS_NAMES[OsType.Linux].commandLine,
    [Builds_SortField.InitiatorName]: COLUMNS_NAMES[OsType.Linux].initiatorName,
    [Builds_SortField.BuildGroup]: COLUMNS_NAMES[OsType.Linux].buildGroup,
    [Builds_SortField.StartTime]: COLUMNS_NAMES[OsType.Linux].startTime,
    [Builds_SortField.Duration]: COLUMNS_NAMES[OsType.Linux].duration,
    [Builds_SortField.FailReason]: COLUMNS_NAMES[OsType.Linux].failReason,
    [Builds_SortField.RemoteTasks]: COLUMNS_NAMES[OsType.Linux].remoteTasks,
    [Builds_SortField.TotalTasks]: COLUMNS_NAMES[OsType.Linux].totalTasks,
    [Builds_SortField.LocalTasks]: COLUMNS_NAMES[OsType.Linux].localTasks,
    [Builds_SortField.CloudTasks]: COLUMNS_NAMES[OsType.Linux].cloudTasks,
    [Builds_SortField.AvgBusyHelpersCores]: COLUMNS_NAMES[OsType.Linux].avgBusyHelpersCores,
    [Builds_SortField.MaxBusyHelpersCores]: COLUMNS_NAMES[OsType.Linux].maxBusyHelpersCores,
    [Builds_SortField.MaxNeededHelpersCores]: COLUMNS_NAMES[OsType.Linux].maxNeededHelpersCores,
  };

  const WORKLOADS_SORT_TO_COLUMNS_MAP = {
    [OsType.Windows]: sortToColumnsWindows,
    [OsType.Linux]: sortToColumnsLinux,
    [OsType.Mac]: sortToColumnsLinux,
  };

  const WORKLOAD_TYPE_SELECTION_OPTIONS = ['Build', 'Rebuild', 'Clean', 'Unknown'];
  const WORKLOADS_CACHE_COLUMNS = ['Hit', 'Miss', 'Uncacheable Tasks'];
  const WORKLOADS_CLOUD_COLUMNS = ['Cloud Tasks'];

  const defaultTableColumnsWindows = [
    windowsColumnsNames.buildResult,
    windowsColumnsNames.startTime,
    windowsColumnsNames.duration,
    windowsColumnsNames.initiatorName,
    windowsColumnsNames.title,
    windowsColumnsNames.commandLine,
    windowsColumnsNames.failedTasks,
    windowsColumnsNames.maxNeededHelpersCores,
    windowsColumnsNames.maxBusyHelpersCores,
    windowsColumnsNames.readyOverActiveRatio,
  ];

  const nonDefaultTableColumnsWindows = [
    windowsColumnsNames.remoteTasks, // remote tasks
    windowsColumnsNames.totalTasks, // total tasks
    windowsColumnsNames.localTasks, // local tasks
    windowsColumnsNames.cloudTasks,
    windowsColumnsNames.distributableTasks,
    windowsColumnsNames.buildGroup,
    windowsColumnsNames.failReason,
    windowsColumnsNames.buildType,
    windowsColumnsNames.cacheTaskHits, // not for linux, also only when there is build-cache data in the full data set
    windowsColumnsNames.cacheTaskMiss, // not for linux, also only when there is build-cache data in the full data set
    windowsColumnsNames.uncacheableTasks, // not for linux, also only when there is build-cache data in the full data set
    windowsColumnsNames.avgBusyHelpersCores,
    windowsColumnsNames.initiatorType,
    windowsColumnsNames.agentDescription,
    windowsColumnsNames.loggedOnUsers,
    windowsColumnsNames.loggingLevel,
  ];

  const defaultTableColumnsLinux = [
    linuxColumnsNames.buildResult,
    linuxColumnsNames.startTime,
    linuxColumnsNames.duration,
    linuxColumnsNames.title,
    linuxColumnsNames.commandLine,
    linuxColumnsNames.initiatorName,
    linuxColumnsNames.maxNeededHelpersCores,
    linuxColumnsNames.maxBusyHelpersCores,
  ];

  const nonDefaultTableColumnsLinux = [
    linuxColumnsNames.totalTasks,
    linuxColumnsNames.remoteTasks,
    linuxColumnsNames.cloudTasks,
    linuxColumnsNames.localTasks,
    linuxColumnsNames.buildGroup,
    linuxColumnsNames.failReason,
    linuxColumnsNames.avgBusyHelpersCores,
  ];

  const nonDefaultTableColumnsMac = [
    linuxColumnsNames.totalTasks,
    linuxColumnsNames.remoteTasks,
    linuxColumnsNames.localTasks,
    linuxColumnsNames.buildGroup,
    linuxColumnsNames.failReason,
    linuxColumnsNames.avgBusyHelpersCores,
  ];

  const DEFAULT_TABLE_COLUMNS = {
    [OsType.Windows]: defaultTableColumnsWindows,
    [OsType.Linux]: defaultTableColumnsLinux,
    [OsType.Mac]: defaultTableColumnsLinux,
  };

  const NON_DEFAULT_TABLE_COLUMNS = {
    [OsType.Windows]: nonDefaultTableColumnsWindows,
    [OsType.Linux]: nonDefaultTableColumnsLinux,
    [OsType.Mac]: nonDefaultTableColumnsMac,
  };

  const COLUMNS = (osType: OsType) => {
    const sortableColumns = WORKLOADS_COLUMNS[osType];
    const defaultColumns = sortableColumns.filter(item => DEFAULT_TABLE_COLUMNS[osType].includes(item.name as keyof typeof windowsColumnsNames));
    const nonDefaultColumns = sortableColumns.filter(item => NON_DEFAULT_TABLE_COLUMNS[osType].includes(item.name as keyof typeof windowsColumnsNames));

    return [...defaultColumns, ...nonDefaultColumns];
  };

  const WORKLOADS_TABLE_WINDOWS_VIEWABLE_COLUMNS = new Set([
    windowsColumnsNames.buildResult,
    windowsColumnsNames.startTime,
    windowsColumnsNames.duration,
    windowsColumnsNames.initiatorName,
    windowsColumnsNames.title,
    windowsColumnsNames.commandLine,
    windowsColumnsNames.failedTasks,
    windowsColumnsNames.maxNeededHelpersCores,
    windowsColumnsNames.maxBusyHelpersCores,

    windowsColumnsNames.initiatorType,
    windowsColumnsNames.buildGroup,
    windowsColumnsNames.totalTasks,
    windowsColumnsNames.remoteTasks,
    windowsColumnsNames.cloudTasks,
    windowsColumnsNames.failReason,
    windowsColumnsNames.buildType,
    windowsColumnsNames.cacheTaskHits,
    windowsColumnsNames.cacheTaskMiss,
    windowsColumnsNames.uncacheableTasks,
    windowsColumnsNames.localTasks,

    windowsColumnsNames.avgBusyHelpersCores,

    windowsColumnsNames.agentDescription,
    windowsColumnsNames.loggedOnUsers,
    windowsColumnsNames.loggingLevel,

    windowsColumnsNames.distributableTasks,
    windowsColumnsNames.readyOverActiveRatio,
  ]);

  const WORKLOADS_TABLE_LINUX_VIEWABLE_COLUMNS = new Set([
    linuxColumnsNames.buildResult,
    linuxColumnsNames.startTime,
    linuxColumnsNames.duration,
    linuxColumnsNames.title,
    linuxColumnsNames.commandLine,
    linuxColumnsNames.initiatorName,
    linuxColumnsNames.buildGroup,
    linuxColumnsNames.totalTasks,
    linuxColumnsNames.remoteTasks,
    linuxColumnsNames.cloudTasks,
    linuxColumnsNames.failReason,
    linuxColumnsNames.localTasks,
    linuxColumnsNames.maxBusyHelpersCores,
    linuxColumnsNames.avgBusyHelpersCores,
    linuxColumnsNames.maxNeededHelpersCores,
  ]);

  const WORKLOADS_TABLE_MAC_VIEWABLE_COLUMNS = new Set([
    linuxColumnsNames.buildResult,
    linuxColumnsNames.startTime,
    linuxColumnsNames.duration,
    linuxColumnsNames.title,
    linuxColumnsNames.commandLine,
    linuxColumnsNames.initiatorName,
    linuxColumnsNames.buildGroup,
    linuxColumnsNames.totalTasks,
    linuxColumnsNames.remoteTasks,
    linuxColumnsNames.failReason,
    linuxColumnsNames.localTasks,
    linuxColumnsNames.maxBusyHelpersCores,
    linuxColumnsNames.avgBusyHelpersCores,
    linuxColumnsNames.maxNeededHelpersCores,
  ]);

  const columns = computed(() => (os.value ? COLUMNS(os.value) : []));

  const viewableColumns = computed(() => {
    const result = {
      [OsType.Windows]: WORKLOADS_TABLE_WINDOWS_VIEWABLE_COLUMNS,
      [OsType.Linux]: WORKLOADS_TABLE_LINUX_VIEWABLE_COLUMNS,
      [OsType.Mac]: WORKLOADS_TABLE_MAC_VIEWABLE_COLUMNS,
    };
    return os.value ? result[os.value] : new Set();
  });

  const defaultColumns = computed(() => (os.value ? new Set(DEFAULT_TABLE_COLUMNS[os.value]) : new Set()));
  const sortableColumns = computed(() => (os.value ? WORKLOADS_COLUMNS[os.value] : {}));
  const mapAgentSortFieldToColumns = computed(() => (os.value ? WORKLOADS_SORT_TO_COLUMNS_MAP[os.value] : {}));
  const mapColumnsToAgentSortField = computed(() => (os.value ? WORKLOADS_COLUMNS_TO_SORT_MAP[os.value] : {}));

  return {
    columns,
    viewableColumns,
    defaultColumns,
    sortableColumns,
    mapAgentSortFieldToColumns,
    mapColumnsToAgentSortField,
    COLUMNS_NAMES,
    WORKLOADS_COLUMNS,
    WORKLOADS_FAIL_REASON,
    WORKLOADS_COLUMNS_TO_SORT_MAP,
    WORKLOADS_SORT_TO_COLUMNS_MAP,
    WORKLOAD_TYPE_SELECTION_OPTIONS,
    WORKLOADS_CACHE_COLUMNS,
    WORKLOADS_CLOUD_COLUMNS,
  };
};

export const STATUS_ICON_MAP = {
  [Builds_BuildResult.Failed]: 'uis-exclamation-circle text-System/app_error',
  [Builds_BuildResult.Success]: 'uis-check-circle text-System/app_primary',
  [Builds_BuildResult.Canceled]: 'uis-times-circle text-Text/text_lowest',
};
